import { LiveToolbar } from "@faire/prototype-playground/platform/components/toolbar/LiveToolbar";
import type { PrototypeMetadata } from "@faire/prototype-playground/workspace/types";
import { useCallback, useEffect, useMemo, useState } from "react";

import type {
  Audience,
  BudgetState,
} from "../../../pages/Ads2DControlsAlphaBeta/shared";
import {
  Toast,
  getGrowthModeLabel,
} from "../../../pages/Ads2DControlsAlphaBeta/shared";

import { AdsManagerContent } from "./components/AdsManagerContent";
import { AdTypesModalProvider } from "./components/AdTypesExplainer";
import { AdvertisingSettingsPage } from "./components/AdvertisingSettingsPage";
import { BrandPortalSidebar } from "./components/BrandPortalSidebar";
import { ConfirmAdvertisingSettingsModal } from "./components/ConfirmAdvertisingSettingsModal";
import { CustomersTab } from "./components/CustomersTab";
import { ForecastModalProvider } from "./components/ForecastExplainer";
import { MonthDetailPageV2 } from "./components/month-detail-v2/MonthDetailPageV2";
import { OverviewTab } from "./components/OverviewTab";
import { PromotedListingsHeader } from "./components/PromotedListingsHeader";
import { PromotedListingsTabs } from "./components/PromotedListingsTabs";
import { StoreTypesModal } from "./components/StoreTypesModal";
import { ADS_MANAGER_SURFACE_CLASS } from "./constants/layout";
import {
  DAIRY_BRAND_PROFILE_TOKEN,
  ELANA_GABRIELLE_PROFILE_TOKEN,
  GOOD_BOOK_PROFILE_TOKEN,
  KAARI_CO_PROFILE_TOKEN,
  KING_FLOYDS_PROFILE_TOKEN,
  MALWEST_PROFILE_TOKEN,
  OGGI_PROFILE_TOKEN,
  P8_PROFILE_TOKEN,
  WOODSY_CRAFT_PROFILE_TOKEN,
  ZOTTER_CHOCOLATES_PROFILE_TOKEN,
  getP8DefaultBudget,
  getP8DefaultGrowthIdx,
  setActiveProfileToken,
} from "./p8AdvertiserData";
import type { OverviewChartId, PlTab, SettingsTab, TopView } from "./types";

export const prototypeMeta: PrototypeMetadata = {
  name: "[ARCHIVED] Ads Manager 2.0 for Listen Labs",
  owner: "Irene Limb",
  email: "irene.limb@faire.com",
  status: "in-progress",
  createdAt: "2026-09-02",
  lastUpdated: "2026-09-02",
  surfaceArea: "Brand",
  category: "experimental",
  creationTool: "cursor",
  thumbnail: "/images/thumbnails/ads-manager-2.png",
  description:
    "Unmoderated test copy of Ads Manager 2.0, forked so the study can be changed without touching the interview prototype.",
};

/** Advertiser-profile selector states (toolbar → active dataset). */
type ProfileState = 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10;

const PROFILE_TOKENS: Record<ProfileState, string> = {
  1: P8_PROFILE_TOKEN,
  2: ELANA_GABRIELLE_PROFILE_TOKEN,
  3: KAARI_CO_PROFILE_TOKEN,
  4: WOODSY_CRAFT_PROFILE_TOKEN,
  5: ZOTTER_CHOCOLATES_PROFILE_TOKEN,
  6: KING_FLOYDS_PROFILE_TOKEN,
  7: OGGI_PROFILE_TOKEN,
  8: GOOD_BOOK_PROFILE_TOKEN,
  9: MALWEST_PROFILE_TOKEN,
  10: DAIRY_BRAND_PROFILE_TOKEN,
};

type SavedSettings = {
  growthIdx: number;
  budget: BudgetState;
  audience: Audience;
  selectedStoreTypes: string[];
  newProductsBoosting: boolean;
};

function createDefaultSavedSettings(): SavedSettings {
  return {
    growthIdx: getP8DefaultGrowthIdx(),
    budget: getP8DefaultBudget(),
    audience: "both",
    selectedStoreTypes: [],
    newProductsBoosting: false,
  };
}

function settingsAreDirty(
  current: SavedSettings,
  saved: SavedSettings
): boolean {
  return (
    current.growthIdx !== saved.growthIdx ||
    current.audience !== saved.audience ||
    current.newProductsBoosting !== saved.newProductsBoosting ||
    current.budget.separateBudget !== saved.budget.separateBudget ||
    current.budget.overallBudget !== saved.budget.overallBudget ||
    current.budget.newCustomerBudget !== saved.budget.newCustomerBudget ||
    current.budget.returningBudget !== saved.budget.returningBudget ||
    current.selectedStoreTypes.join("\0") !==
      saved.selectedStoreTypes.join("\0")
  );
}

const AdsManager2Page: React.FC = () => {
  const [topView, setTopView] = useState<TopView>("promoted-listings");
  const [plTab, setPlTab] = useState<PlTab>("overview");
  const [settingsTab, setSettingsTab] = useState<SettingsTab>("core");
  const [selectedMonthIndex, setSelectedMonthIndex] = useState<number | null>(
    null
  );
  const [selectedStoreTypes, setSelectedStoreTypes] = useState<string[]>([]);
  const [newProductsBoosting, setNewProductsBoosting] = useState(false);
  // Dairy Brand. Must stay in step with the module-level default in
  // p8AdvertiserData.ts: the budget and growth useState initializers below read
  // the active token during this same first render, so if the two disagree the
  // page opens showing one brand's numbers under another brand's settings.
  const [profileState, setProfileState] = useState<ProfileState>(10);
  // Study build: fixed, not state. The chart-slot picker is gone from the
  // toolbar, so every session sees the same two charts in the same places —
  // otherwise a participant could rearrange the page mid-task and their
  // session would stop being comparable to anyone else's.
  const leftChart: OverviewChartId = "no-forecast-monthly";
  const rightChart: OverviewChartId = "roas";
  const budgetConstrained = false;
  const [modalOpen, setModalOpen] = useState(false);
  const [growthIdx, setGrowthIdx] = useState(getP8DefaultGrowthIdx());
  const [budget, setBudget] = useState<BudgetState>(getP8DefaultBudget());
  const [audience, setAudience] = useState<Audience>("both");
  const [savedSettings, setSavedSettings] = useState<SavedSettings>(() =>
    createDefaultSavedSettings()
  );
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [toastMessage, setToastMessage] = useState("Settings saved");
  const [showToast, setShowToast] = useState(false);

  const showToastWithMessage = useCallback((message: string) => {
    setToastMessage(message);
    setShowToast(true);
    window.setTimeout(() => setShowToast(false), 4000);
  }, []);

  const currentSettings = useMemo<SavedSettings>(
    () => ({
      growthIdx,
      budget,
      audience,
      selectedStoreTypes,
      newProductsBoosting,
    }),
    [growthIdx, budget, audience, selectedStoreTypes, newProductsBoosting]
  );

  const isDirty = settingsAreDirty(currentSettings, savedSettings);

  useEffect(() => {
    document.title = prototypeMeta.name;
  }, []);

  const handleProfileChange = useCallback((state: ProfileState) => {
    // Set the module-level active token synchronously BEFORE the state update so
    // the re-render reads the new profile. (Do not mirror this in an effect keyed
    // on profileState — a stale-closure effect would clobber the token post-render.)
    setActiveProfileToken(PROFILE_TOKENS[state]);
    const defaultBudget = getP8DefaultBudget();
    const defaultGrowthIdx = getP8DefaultGrowthIdx();
    setProfileState(state);
    setBudget(defaultBudget);
    setGrowthIdx(defaultGrowthIdx);
    setSavedSettings((prev) => ({
      ...prev,
      budget: defaultBudget,
      growthIdx: defaultGrowthIdx,
    }));
  }, []);

  const openSettings = (
    tab: SettingsTab = "core",
    options?: { openStoreTypesModal?: boolean }
  ) => {
    setTopView("advertising-settings");
    setSettingsTab(tab);
    if (options?.openStoreTypesModal) {
      setModalOpen(true);
    }
  };

  const openMonthDetail = useCallback((monthIndex: number) => {
    setSelectedMonthIndex(monthIndex);
    setTopView("month-detail");
    window.scrollTo({ top: 0 });
  }, []);

  const handleStoreTypesConfirm = (types: string[]) => {
    setSelectedStoreTypes(types);
    setModalOpen(false);
  };

  const handleUndoSettings = useCallback(() => {
    setGrowthIdx(savedSettings.growthIdx);
    setBudget(savedSettings.budget);
    setAudience(savedSettings.audience);
    setSelectedStoreTypes(savedSettings.selectedStoreTypes);
    setNewProductsBoosting(savedSettings.newProductsBoosting);
  }, [savedSettings]);

  const handleConfirmSettings = useCallback(() => {
    setSavedSettings(currentSettings);
    setConfirmModalOpen(false);
    showToastWithMessage("Settings saved");
  }, [currentSettings, showToastWithMessage]);

  return (
    <>
      {/* Study build: one group left. The Cohort view and Overview charts
          groups are gone, and Chocolate Brand is off the profile list — the
          toolbar is a live control panel a participant can reach, so anything
          it can change is something a session can differ by.

          Dropping a state from a group also closes its URL: the toolbar only
          applies a `states=` param whose number is still listed, so
          `states=profile:5` and `states=cohort:1` now fall back to the
          defaults instead of reaching the hidden variants. */}
      <LiveToolbar
        stateGroups={[
          {
            title: "Advertiser profile",
            queryKey: "profile",
            description:
              "The advertiser dataset backing the prototype — an anonymized archetype or a real brand's latest data.",
            // The gaps in the state numbers are deliberate: `queryKey` puts the
            // number in the URL, so renumbering what's left would silently
            // repoint links someone has already shared.
            states: [
              { stateNumber: 10, label: "Dairy Brand" },
              { stateNumber: 2, label: "Textile Brand" },
            ],
            active: profileState,
            onChange: (state) => handleProfileChange(state as ProfileState),
          },
        ]}
      />

      {/* Both wrap the whole surface because both explainers are reachable from
          more than one place: the forecast one from the Overview's month table,
          its ROAS legend, and the v2 cohort view; the ad types one from the ad
          type filter's info button and from the Total ad sales tooltip. One
          dialog each between all of those, rather than one per trigger — and a
          dialog opened from inside a tooltip panel cannot be owned by it, since
          the panel unmounts as soon as the pointer leaves. */}
      <ForecastModalProvider>
        <AdTypesModalProvider>
          <div
            className={`flex h-dvh min-h-0 overflow-hidden text-[#333] ${ADS_MANAGER_SURFACE_CLASS}`}
          >
            <BrandPortalSidebar />
            <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden">
              {topView === "advertising-settings" ? (
                <AdvertisingSettingsPage
                  settingsTab={settingsTab}
                  onSettingsTabChange={setSettingsTab}
                  onBack={() => setTopView("promoted-listings")}
                  growthIdx={growthIdx}
                  onGrowthIdxChange={setGrowthIdx}
                  budget={budget}
                  onBudgetChange={(next) =>
                    setBudget((prev) => ({ ...prev, ...next }))
                  }
                  audience={audience}
                  onAudienceChange={setAudience}
                  selectedStoreTypes={selectedStoreTypes}
                  newProductsBoosting={newProductsBoosting}
                  budgetConstrained={budgetConstrained}
                  onSelectStoreTypes={() => setModalOpen(true)}
                  onNewProductsBoostingChange={setNewProductsBoosting}
                  isDirty={isDirty}
                  onSave={() => setConfirmModalOpen(true)}
                  onUndo={handleUndoSettings}
                />
              ) : topView === "month-detail" && selectedMonthIndex != null ? (
                /* Study build: v2 unconditionally. v1 is still on disk under
                   components/month-detail, just unreachable. */
                <MonthDetailPageV2
                  monthIndex={selectedMonthIndex}
                  onBack={() => {
                    setTopView("promoted-listings");
                    setPlTab("overview");
                  }}
                />
              ) : (
                <>
                  <PromotedListingsHeader
                    onOpenSettings={() => openSettings()}
                  />
                  <PromotedListingsTabs
                    activeTab={plTab}
                    onTabChange={setPlTab}
                  />
                  <div
                    className={`min-h-0 flex-1 overflow-y-auto ${ADS_MANAGER_SURFACE_CLASS}`}
                  >
                    {plTab === "overview" ? (
                      <OverviewTab
                        onOpenMonth={openMonthDetail}
                        leftChart={leftChart}
                        rightChart={rightChart}
                      />
                    ) : null}
                    {plTab === "customers" ? <CustomersTab /> : null}
                    {/* Study build: the tab stays in the row so the nav looks
                        whole, but its content is out of scope for the test.
                        Saying so beats an empty tab, which reads as a bug a
                        participant then reports instead of moving on. */}
                    {plTab === "products" ? (
                      <AdsManagerContent className="py-8">
                        <div className="bg-fs-surface-primary rounded-lg border border-[#dfe0e1] p-6">
                          <p className="text-sm leading-5 text-[#757575]">
                            Not relevant for this test.
                          </p>
                        </div>
                      </AdsManagerContent>
                    ) : null}
                  </div>
                </>
              )}
            </div>
          </div>
        </AdTypesModalProvider>
      </ForecastModalProvider>

      <StoreTypesModal
        open={modalOpen}
        selected={selectedStoreTypes}
        onClose={() => setModalOpen(false)}
        onConfirm={handleStoreTypesConfirm}
        onSuggestionSubmitted={() =>
          showToastWithMessage("Suggestion submitted")
        }
      />

      <ConfirmAdvertisingSettingsModal
        open={confirmModalOpen}
        growthModeLabel={getGrowthModeLabel("A", growthIdx)}
        budget={budget}
        audience={audience}
        selectedStoreTypes={selectedStoreTypes}
        newProductsBoosting={newProductsBoosting}
        onCancel={() => setConfirmModalOpen(false)}
        onConfirm={handleConfirmSettings}
      />

      <Toast message={toastMessage} visible={showToast} />
    </>
  );
};

export default AdsManager2Page;
